import { useCallback, type MouseEvent } from 'react'
import { twMerge } from 'tailwind-merge'
import { formatTimeAgo } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { useViewActions } from '../../view-atoms'
import { MEETING_VIEW_ID } from '../meeting/meeting-view-id'
import { getSourceIcon } from './utils/get-source-icon'
import { getSourceLabel } from './utils/get-source-label'
import type { ReminderItem } from '../../hooks/use-reminders'

interface ReminderSourceProps {
  item: ReminderItem
  className?: string
  /**
   * Wrap a long origin onto another line instead of ellipsizing. The hover card
   * has the width for it; the cell row stays a single truncated line.
   */
  wrap?: boolean
}

/**
 * Origin attribution for a reminder — "<source> · <time ago>" — owning the
 * navigation back to whatever produced it. The counterpart to `ActionSource`,
 * and shared by the cell and its hover card so the two can't drift.
 */
export function ReminderSource({ item, className, wrap = false }: ReminderSourceProps) {
  const { openView } = useViewActions()
  const source = item.source

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      if (!source) return
      if (source.type === 'audioNote') {
        openView(
          MEETING_VIEW_ID,
          { mode: 'note', audioNoteId: source.id, title: getSourceLabel(source) },
          { sourceLabel: 'Reminders' },
        )
      } else if (source.type === 'conversation') {
        openView('chat', { chatId: source.id }, { sourceLabel: 'Reminders' })
      }
    },
    [source, openView],
  )

  if (!source) {
    return null
  }

  const label = (
    <>
      <span className="text-foreground-secondary shrink-0">{getSourceIcon(source.type)}</span>
      <span className={twMerge('type-ui-xs min-w-0', wrap ? 'break-words' : 'truncate')}>
        <span
          className={twMerge(
            'text-foreground-secondary font-medium',
            // Underline only where the label actually navigates.
            isClickable(item) &&
              'underline decoration-transparent transition-[text-decoration-color] duration-150 group-hover/source:decoration-current',
          )}
        >
          {getSourceLabel(source)}
        </span>
        <span className="text-foreground-placeholder"> · {formatTimeAgo(item.createdAt)}</span>
      </span>
    </>
  )

  const rootClass = twMerge('flex w-full min-w-0 gap-1.5', wrap ? 'items-start' : 'items-center', className)

  if (!isClickable(item)) {
    return <div className={rootClass}>{label}</div>
  }

  return (
    <button
      onClick={handleClick}
      className={twMerge('group/source pointer-events-auto bg-transparent text-left', rootClass)}
    >
      {label}
    </button>
  )
}

/** Only meeting notes and chats have somewhere to open. */
export function isClickable(item: ReminderItem): boolean {
  return item.source?.type === 'audioNote' || item.source?.type === 'conversation'
}
